import { CommonModule } from '@angular/common';
import { Component, signal } from '@angular/core';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { AgGridModule, ICellRendererAngularComp } from 'ag-grid-angular';
import { ICellRendererParams } from 'ag-grid-community';

import { PrismIconComponent, PrismProgressIndicatorComponent } from '@shure/prism-angular-components';
import { shUseTranslation } from '@shure/shared/angular/utils/i18n';

export enum ProgressStatus {
	InProgress = 'in-progress',
	Success = 'success',
	Error = 'error'
}

export interface ProgressCellData {
	/**
	 * Current progress status, determines which icon to display
	 */
	status: ProgressStatus;
	/**
	 * Transloco key to use for rendering the status text
	 */
	statusTextKey?: string;
}

export type ProgressRendererComponentParams = ICellRendererParams<ProgressCellData>;

/**
 * Shared cell renderer for displaying progress status (in progress, error, success) in an AG Grid table.
 * Renders an icon or indeterminate progress spinner as well as an optional status message.
 */
@Component({
	selector: 'sh-progress-renderer',
	templateUrl: './progress-renderer.component.html',
	styleUrls: ['./progress-renderer.component.scss'],
	imports: [AgGridModule, CommonModule, TranslocoModule, PrismIconComponent, PrismProgressIndicatorComponent]
})
@shUseTranslation('shAgGrid', /* istanbul ignore next */ (lang) => import(`../../../i18n/${lang}`))
export class ProgressRendererComponent implements ICellRendererAngularComp {
	public cellData = signal<ProgressCellData | undefined>(undefined);
	protected readonly progressStatusEnum = ProgressStatus;

	constructor(public readonly translocoService: TranslocoService) {}

	public agInit(params: ProgressRendererComponentParams): void {
		this.cellData.set(params.value);
	}

	public refresh(params: ProgressRendererComponentParams): boolean {
		this.cellData.set(params.value);
		return true;
	}

	public getStatusText(labelKey: string | undefined): string {
		if (labelKey) {
			return this.translocoService.translate(labelKey);
		}
		return '';
	}
}
